import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Wrench, 
  Droplet, 
  Flame, 
  Sparkles, 
  Star,
  Phone,
  CheckCircle,
  ArrowRight,
  Play,
  Image as ImageIcon,
  Users,
  Award,
  Hammer,
  Shield
} from 'lucide-react';

const BathroomKitchenRemodel = () => {
  const navigate = useNavigate();
  
  const handleContactClick = () => {
    navigate('/#contact');
    // Small delay to ensure navigation happens first
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };
  useEffect(() => {
    // Meta tags are now handled by static generation in generate-static.cjs
    // This ensures consistency and avoids conflicts between React and static HTML

    // Add Bathroom & Kitchen Remodel Service Schema for AI SEO
    const remodelServiceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://colorado-plumbing.com/bathroom-kitchen-remodel#service',
      'name': 'Bathroom & Kitchen Remodel Plumbing Services',
      'description': 'Expert plumbing services for bathroom and kitchen remodels in Denver Metro. Luxury fixture installation, layout changes, gas line work, sewer modifications, and custom plumbing solutions.',
      'provider': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'email': 'codee@colorado-plumbing.com',
        'url': 'https://colorado-plumbing.com',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': '5108 Sydney Ave',
          'addressLocality': 'Littleton',
          'addressRegion': 'CO',
          'postalCode': '80130',
          'addressCountry': 'US'
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.8',
          'reviewCount': '360',
          'bestRating': '5',
          'worstRating': '1'
        }
      },
      'areaServed': [
        {
          '@type': 'City',
          'name': 'Denver',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Highlands Ranch',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Littleton',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Lakewood',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Arvada',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Thornton',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Longmont',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Aurora',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Lone Tree',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Castle Rock',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Englewood',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Centennial',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Westminster',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Loveland',
          'addressRegion': 'CO'
        }
      ],
      'serviceType': 'Remodel Plumbing',
      'category': 'Plumbing Services',
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Bathroom & Kitchen Remodel Plumbing Services',
        'itemListElement': [
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Luxury Fixture Installation',
              'description': 'High-end faucets, showers, tubs, and custom plumbing fixture installation'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Plumbing Layout Changes',
              'description': 'Moving pipes and drains for new bathroom and kitchen layouts'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Gas Line Installation',
              'description': 'Gas line installation for ranges, outdoor grills, and fireplaces'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Sewer & Drain Modifications',
              'description': 'Sewer line modifications and new drain installations for remodels'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Whole Home Repiping',
              'description': 'Complete home repiping during major renovation projects'
            }
          }
        ]
      },
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'USD',
        'availability': 'https://schema.org/InStock',
        'description': 'Professional remodel plumbing services with luxury fixture expertise and transparent pricing'
      },
      'additionalProperty': [
        {
          '@type': 'PropertyValue',
          'name': 'Master Plumber Owned',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Luxury Fixture Specialist',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Layout Change Expert',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Gas Line Certified',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Pricing Transparency',
          'value': 'Complete pricing guide online'
        }
      ]
    };

    // Inject the service schema
    let serviceSchemaScript = document.querySelector('#remodel-service-schema');
    if (serviceSchemaScript) {
      serviceSchemaScript.textContent = JSON.stringify(remodelServiceSchema);
    } else {
      serviceSchemaScript = document.createElement('script');
      serviceSchemaScript.setAttribute('id', 'remodel-service-schema');
      serviceSchemaScript.setAttribute('type', 'application/ld+json');
      serviceSchemaScript.textContent = JSON.stringify(remodelServiceSchema);
      document.head.appendChild(serviceSchemaScript);
    }
  }, []);

  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    // Load the BirdEye widget after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      loadBirdEyeWidget();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-950">
      <Header />
      <main>
        <section className="pt-56 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] bg-repeat"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center pt-8 pb-8">
              <div className="flex items-center justify-center mb-6">
                <Sparkles className="mr-3 text-action-warm" size={40} />
                <span className="text-sm font-medium bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full border border-white/30">
                  Remodel Plumbing Specialists
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-neutral-white">
                Denver's Expert Bathroom & Kitchen Remodel Plumbing
              </h1>

              <p className="text-xl mb-8 text-trust-light/90">
                Master plumber owned with specialized expertise in luxury remodel projects. From high-end fixture installations to complex layout changes, 
                we handle every aspect of your <Link to="/residential-plumbing" className="text-action-warm hover:text-white underline">residential remodel</Link> with 
                professional coordination. When layouts require <Link to="/sewer-repair-replacement" className="text-action-warm hover:text-white underline">sewer line modifications</Link>, 
                we've got you covered. <Link to="/pricing" className="text-action-warm hover:text-white underline">Transparent pricing</Link> for all remodel services.
              </p>
              
              <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
                <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
                <div id="birdeye-fallback" style={{ display: 'none' }} className="text-trust-light">
                  <p>Reviews loading... See our 310+ verified remodel project reviews on Google and BirdEye.</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <button 
                  onClick={handleContactClick}
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                >
                  Get Remodel Estimate
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Colorado Remodel Expertise Section */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Colorado Remodel Plumbing Expertise
              </h2>
              <p className="text-lg text-blue-100">
                Colorado's unique environment presents specific challenges for bathroom and kitchen remodels. Our master plumber owned company 
                understands the Front Range's aging infrastructure, expansive clay soils, severe freeze-thaw cycles, and complex municipal 
                code requirements that affect every remodel project.
              </p>
            </div>
            
            {/* 4-column grid highlighting Colorado challenges */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {/* Aging Infrastructure Challenge */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-red-50 w-fit mb-4">
                  <Shield className="text-red-600" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Aging Infrastructure</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Many Colorado homes built before 1960 have galvanized steel, lead, or polybutylene pipes that require complete replacement during remodels.
                </p>
                <div className="text-xs text-blue-600 font-semibold">
                  ✓ Expert material identification<br/>
                  ✓ Safe lead pipe removal<br/>
                  ✓ Complete system upgrades
                </div>
              </div>

              {/* Climate Impact Challenge */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-blue-50 w-fit mb-4">
                  <Droplet className="text-blue-600" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Freeze-Thaw Cycles</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Colorado's brutal temperature fluctuations and harsh winters require specialized material selection and installation techniques.
                </p>
                <div className="text-xs text-blue-600 font-semibold">
                  ✓ PEX tubing specialists<br/>
                  ✓ Proper insulation techniques<br/>
                  ✓ Freeze prevention strategies
                </div>
              </div>

              {/* Hard Water Challenge */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-orange-50 w-fit mb-4">
                  <Wrench className="text-orange-600" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Hard Water Issues</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Colorado's mineral-rich water causes scale buildup that reduces flow and shortens appliance lifespan in remodel projects.
                </p>
                <div className="text-xs text-blue-600 font-semibold">
                  ✓ Water softening solutions<br/>
                  ✓ Scale prevention systems<br/>
                  ✓ Appliance protection
                </div>
              </div>

              {/* Expansive Clay Soils Challenge */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-green-50 w-fit mb-4">
                  <Hammer className="text-green-600" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Expansive Clay Soils</h3>
                <p className="text-gray-600 text-sm mb-3">
                  Colorado's clay soils expand and contract with moisture changes, stressing pipes and requiring specialized installation methods.
                </p>
                <div className="text-xs text-blue-600 font-semibold">
                  ✓ Soil impact assessment<br/>
                  ✓ Flexible pipe materials<br/>
                  ✓ Foundation protection
                </div>
              </div>
            </div>

            {/* Municipal Code Expertise */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-8 shadow-lg border border-gray-100">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold mb-4 text-white">
                  Navigating Colorado's Complex Municipal Codes
                </h3>
                <p className="text-gray-700 max-w-3xl mx-auto">
                  Each Front Range municipality adopts different amendments to the International Plumbing Code. We maintain current knowledge 
                  of code variations across <Link to="/denver-plumber" className="text-trust-primary underline hover:text-action-primary">Denver</Link>, 
                  <Link to="/highlands-ranch-plumber" className="text-trust-primary underline hover:text-action-primary"> Highlands Ranch</Link>, 
                  <Link to="/littleton-plumber" className="text-trust-primary underline hover:text-action-primary"> Littleton</Link>, 
                  <Link to="/lakewood-plumber" className="text-trust-primary underline hover:text-action-primary"> Lakewood</Link>, 
                  <Link to="/arvada-plumber" className="text-trust-primary underline hover:text-action-primary"> Arvada</Link>, 
                  <Link to="/thornton-plumber" className="text-trust-primary underline hover:text-action-primary"> Thornton</Link>, 
                  <Link to="/longmont-plumber" className="text-trust-primary underline hover:text-action-primary"> Longmont</Link>, 
                  <Link to="/castle-rock-plumber" className="text-trust-primary underline hover:text-action-primary"> Castle Rock</Link>, and 
                  <Link to="/centennial-plumber" className="text-trust-primary underline hover:text-action-primary"> Centennial</Link>.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-blue-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-blue-600" size={32} />
                  </div>
                  <h4 className="font-bold mb-2">Digital Permit Expertise</h4>
                  <p className="text-sm text-gray-600">Proficient with eTRAKiT and municipal online portals for efficient permit processing</p>
                </div>
                <div className="text-center">
                  <div className="bg-green-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Award className="text-green-600" size={32} />
                  </div>
                  <h4 className="font-bold mb-2">Local Code Knowledge</h4>
                  <p className="text-sm text-gray-600">Current with municipal amendments and inspection requirements across Metro Denver</p>
                </div>
                <div className="text-center">
                  <div className="bg-orange-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Users className="text-orange-600" size={32} />
                  </div>
                  <h4 className="font-bold mb-2">Inspector Relationships</h4>
                  <p className="text-sm text-gray-600">Established relationships with local inspectors for smooth project approval</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 md:py-20 bg-gradient-to-bl from-blue-900 to-blue-950 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Complete Remodel Plumbing Services</h2>
              <p className="text-lg text-blue-100">
                We specialize in the plumbing aspects of bathroom and kitchen remodels, working seamlessly with general contractors, 
                designers, and homeowners across <Link to="/highlands-ranch-plumber" className="text-trust-primary underline hover:text-action-primary">Highlands Ranch</Link>, 
                <Link to="/littleton-plumber" className="text-trust-primary underline hover:text-action-primary"> Littleton</Link>, 
                <Link to="/denver-plumber" className="text-trust-primary underline hover:text-action-primary"> Denver</Link>, and all Metro areas.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Luxury Fixture Installation */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-blue-50 w-fit mb-4">
                  <Sparkles className="text-blue-600" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3">Luxury Fixture Installation</h3>
                <p className="text-gray-600 mb-4">High-end fixture installations for bathrooms and kitchens with precision and care. Professional installations in <Link to="/arvada-plumber" className="text-trust-primary underline hover:text-action-primary">Arvada</Link>, <Link to="/lakewood-plumber" className="text-trust-primary underline hover:text-action-primary">Lakewood</Link>, and surrounding areas.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">Designer Toilets & Bidets</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">Luxury Tubs & Walk-in Showers</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">High-End Kitchen & Bath Faucets</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">Kitchen Island Sinks & Pot Fillers</span>
                  </li>
                </ul>
              </div>

              {/* Layout Changes & Line Relocation */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-green-50 w-fit mb-4">
                  <Wrench className="text-green-600" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3">Layout Changes & Line Relocation</h3>
                <p className="text-gray-600 mb-4">Expert water line and drain line relocations for new bathroom and kitchen layouts. Complex relocations in <Link to="/thornton-plumber" className="text-trust-primary underline hover:text-action-primary">Thornton</Link>, <Link to="/longmont-plumber" className="text-trust-primary underline hover:text-action-primary">Longmont</Link>, and throughout Metro Denver.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">Water Line Relocation & Extension</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">Drain Line Rerouting & Installation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">
                      <Link to="/sewer-repair-replacement" className="text-blue-600 hover:text-blue-800">Sewer Line Modifications</Link>
                    </span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">Bathroom & Kitchen Rough-in Plumbing</span>
                  </li>
                </ul>
              </div>

              {/* Gas Line & Appliance Connections */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="p-3 rounded-lg bg-orange-50 w-fit mb-4">
                  <Flame className="text-orange-600" size={28} />
                </div>
                <h3 className="text-xl font-bold mb-3">Gas Line & Appliance Work</h3>
                <p className="text-gray-600 mb-4">Safe gas line installations and relocations for kitchen remodel appliance connections. Professional gas work with <Link to="/pricing" className="text-trust-primary underline hover:text-action-primary">transparent pricing</Link> throughout Denver Metro.</p>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">Gas Line Installation & Relocation</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">Gas Range & Cooktop Connections</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">Gas Fireplace Connections</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm">
                      <Link to="/water-heater-pricing" className="text-blue-600 hover:text-blue-800">Appliance Water Line Connections</Link>
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Client Segment Solutions */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Specialized Solutions for Every Client Type
              </h2>
              <p className="text-lg text-blue-100">
                Different clients have unique needs during bathroom and kitchen remodels. Our expertise serves homeowners, 
                general contractors, and property investors with tailored solutions for their specific challenges.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Homeowner Solutions */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <div className="p-3 rounded-lg bg-blue-100 w-fit mb-6">
                  <Users className="text-blue-700" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Homeowner Projects</h3>
                <p className="text-gray-700 mb-6">
                  Homeowners need trusted advisors who protect their investment and ensure long-term functionality while 
                  navigating complex decisions about fixture selection and layout optimization.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-sm">Investment Protection</h4>
                      <p className="text-sm text-gray-600">Comprehensive inspections identify hidden problems before they become expensive surprises</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-sm">Design Consultation</h4>
                      <p className="text-sm text-gray-600">Expert guidance on fixture selection and layout optimization for maximum functionality</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-sm">Code Compliance Assurance</h4>
                      <p className="text-sm text-gray-600">Navigate complex municipal requirements to avoid fines and costly rework</p>
                    </div>
                  </div>
                </div>
                
                <Link 
                  to="/pricing" 
                  className="inline-flex items-center text-blue-700 font-semibold hover:text-blue-900"
                >
                  View Transparent Pricing <ArrowRight className="ml-2" size={16} />
                </Link>
              </div>

              {/* General Contractor Solutions */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 border border-green-100">
                <div className="p-3 rounded-lg bg-green-100 w-fit mb-6">
                  <Hammer className="text-green-700" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">General Contractors</h3>
                <p className="text-gray-700 mb-6">
                  General contractors need reliable plumbing partners who deliver precise coordination, on-schedule completion, 
                  and seamless integration with other trades for complex remodel projects.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-sm">Trade Coordination</h4>
                      <p className="text-sm text-gray-600">Expert scheduling with framing, electrical, and HVAC crews for efficient project flow</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-sm">Permit Expertise</h4>
                      <p className="text-sm text-gray-600">Handle complex municipal permitting across Denver Metro jurisdictions</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-sm">Problem Resolution</h4>
                      <p className="text-sm text-gray-600">Quick solutions for unexpected issues to keep projects on schedule and budget</p>
                    </div>
                  </div>
                </div>
                
                <Link 
                  to="/commercial-plumbing" 
                  className="inline-flex items-center text-green-700 font-semibold hover:text-green-900"
                >
                  Contractor Services <ArrowRight className="ml-2" size={16} />
                </Link>
              </div>

              {/* Property Investor Solutions */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border border-orange-100">
                <div className="p-3 rounded-lg bg-orange-100 w-fit mb-6">
                  <Award className="text-orange-700" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Property Investors</h3>
                <p className="text-gray-700 mb-6">
                  Property investors require cost-effective solutions that maximize ROI while ensuring reliability. 
                  Our <Link to="/pricing" className="text-orange-700 underline hover:text-orange-900">transparent pricing</Link> helps with accurate project budgeting.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-sm">ROI Optimization</h4>
                      <p className="text-sm text-gray-600">Strategic fixture upgrades that maximize property value while controlling costs</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-sm">Bulk Project Pricing</h4>
                      <p className="text-sm text-gray-600">Volume discounts for multiple properties with consistent quality standards</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="text-green-500 mr-3 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="font-semibold text-sm">Quick Turnaround</h4>
                      <p className="text-sm text-gray-600">Efficient project completion to minimize vacancy periods and maximize rental income</p>
                    </div>
                  </div>
                </div>
                
                <Link 
                  to="/reviews" 
                  className="inline-flex items-center text-orange-700 font-semibold hover:text-orange-900"
                >
                  Investor Testimonials <ArrowRight className="ml-2" size={16} />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Common Issues & Our Expert Solutions */}
        <section className="py-20 bg-gradient-to-bl from-blue-900 to-blue-950 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                Common Remodel Issues & Our Expert Solutions
              </h2>
              <p className="text-lg text-blue-100">
                Colorado remodels often uncover complex plumbing challenges that require specialized expertise. 
                Here's how we solve the most common issues encountered in Front Range bathroom and kitchen projects.
              </p>
            </div>
            
            <div className="space-y-8">
              {/* Aging Infrastructure Issue */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-8 shadow-lg border-l-4 border-red-500">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-red-50 mr-4">
                        <Shield className="text-red-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Problem: Outdated Pipe Materials</h3>
                    </div>
                    <p className="text-gray-700 mb-4">
                      Many Colorado homes built before 1960 contain galvanized steel pipes (prone to corrosion), 
                      lead pipes (health hazard), or polybutylene pipes (chemical failure with chlorine). These materials 
                      cause low water pressure, leaks, and potential health risks during remodels.
                    </p>
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-red-800 text-sm font-semibold">⚠ Warning Signs:</p>
                      <ul className="text-red-700 text-sm mt-2">
                        <li>• Rust-colored water or metallic taste</li>
                        <li>• Frequent pipe leaks and repairs</li>
                        <li>• Severely reduced water pressure</li>
                        <li>• Gray or blue plastic pipes (polybutylene)</li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-green-50 mr-4">
                        <CheckCircle className="text-green-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-success-primary">Our Expert Solution</h3>
                    </div>
                    <p className="text-gray-700 mb-4">
                      Complete system evaluation followed by strategic whole-house repiping using modern materials. 
                      We specialize in <strong>PEX tubing installations</strong> that resist Colorado's freeze-thaw cycles 
                      and expansive clay soil movement.
                    </p>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-green-800 text-sm font-semibold">✓ Our Process:</p>
                      <ul className="text-green-700 text-sm mt-2">
                        <li>• Comprehensive material identification inspection</li>
                        <li>• Safe lead pipe removal with proper containment</li>
                        <li>• PEX repiping with freeze-protection strategies</li>
                        <li>• Pressure testing and municipal permit coordination</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hard Water & Mineral Buildup Issue */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-8 shadow-lg border-l-4 border-blue-500">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-blue-50 mr-4">
                        <Droplet className="text-blue-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Problem: Hard Water Scale Buildup</h3>
                    </div>
                    <p className="text-gray-700 mb-4">
                      Colorado's mineral-rich water creates scale deposits that accumulate in pipes, reduce water flow, 
                      and shorten the lifespan of water heaters and appliances. This is especially problematic in 
                      luxury remodels with high-end fixtures and appliances.
                    </p>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-800 text-sm font-semibold">⚠ Impact on Remodels:</p>
                      <ul className="text-blue-700 text-sm mt-2">
                        <li>• New fixtures quickly develop mineral deposits</li>
                        <li>• Reduced water pressure throughout the home</li>
                        <li>• Shortened appliance and water heater lifespan</li>
                        <li>• Higher utility bills from inefficient operation</li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-green-50 mr-4">
                        <CheckCircle className="text-green-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-success-primary">Our Expert Solution</h3>
                    </div>
                    <p className="text-gray-700 mb-4">
                      Integrated water conditioning systems designed specifically for Colorado water conditions. 
                      We install whole-house water softening and filtration systems that protect your remodel investment 
                      and extend appliance life.
                    </p>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-green-800 text-sm font-semibold">✓ Our Approach:</p>
                      <ul className="text-green-700 text-sm mt-2">
                        <li>• Water quality testing and system sizing</li>
                        <li>• Strategic placement during remodel phase</li>
                        <li>• Integration with new plumbing systems</li>
                        <li>• Long-term maintenance planning</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Layout Changes & Code Compliance Issue */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-8 shadow-lg border-l-4 border-purple-500">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-purple-50 mr-4">
                        <Wrench className="text-purple-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Problem: Complex Layout Changes</h3>
                    </div>
                    <p className="text-gray-700 mb-4">
                      Modern remodels often require moving plumbing to accommodate new layouts, but Colorado's varying 
                      municipal codes and unique soil conditions make line relocations complex. Poor planning can result 
                      in code violations, expensive rework, and structural damage.
                    </p>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-purple-800 text-sm font-semibold">⚠ Common Challenges:</p>
                      <ul className="text-purple-700 text-sm mt-2">
                        <li>• Different code requirements across municipalities</li>
                        <li>• Venting complications with layout changes</li>
                        <li>• Structural modifications affecting drainage slope</li>
                        <li>• Integration with existing systems</li>
                      </ul>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-green-50 mr-4">
                        <CheckCircle className="text-green-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-success-primary">Our Expert Solution</h3>
                    </div>
                    <p className="text-gray-700 mb-4">
                      Comprehensive layout planning with municipal code expertise across all Denver Metro jurisdictions. 
                      Our <Link to="/meet-the-team" className="text-purple-600 underline hover:text-purple-800">master plumber team</Link> handles 
                      complex relocations while ensuring code compliance and proper drainage.
                    </p>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-green-800 text-sm font-semibold">✓ Our Process:</p>
                      <ul className="text-green-700 text-sm mt-2">
                        <li>• Municipal code research and permit planning</li>
                        <li>• 3D layout design and drainage calculations</li>
                        <li>• Structural integration and support planning</li>
                        <li>• Inspector coordination and approval management</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center mt-12">
              <p className="text-lg text-blue-100 mb-6">
                Every remodel presents unique challenges. Our Colorado expertise helps identify and solve problems 
                before they become expensive surprises.
              </p>
              <Link 
                to="/pricing"
                className="inline-flex items-center px-8 py-4 bg-trust-primary text-white rounded-lg font-semibold hover:bg-authority-navy transition-colors"
              >
                See Our Transparent Pricing
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* Competitive Advantages */}
        <section className="py-20 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Why Choose Colorado First for Your Remodel?</h2>
              <p className="text-lg text-blue-100">
                Our master plumber owned company combines specialized Colorado expertise with proven remodel experience. 
                We understand the unique challenges of Front Range plumbing and deliver solutions that protect your investment 
                while ensuring long-term performance.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Master Plumber Colorado Expertise */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-8 shadow-lg border border-gray-100">
                <div className="p-3 rounded-lg bg-blue-50 w-fit mb-4">
                  <Award className="text-blue-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Master Plumber Colorado Expertise</h3>
                <p className="text-gray-700 mb-4">
                  Unlike general contractors who subcontract plumbing, we're master plumber owned with deep expertise in Colorado's 
                  unique environmental challenges. Our team understands expansive clay soils, freeze-thaw cycles, hard water impacts, 
                  and the complex municipal code variations across Denver Metro.
                </p>
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 font-semibold mb-2">✓ Colorado-Specific Solutions:</p>
                  <ul className="text-blue-700 text-sm space-y-1">
                    <li>• PEX installation expertise for freeze-thaw resilience</li>
                    <li>• Expansive clay soil mitigation strategies</li>
                    <li>• Hard water system integration planning</li>
                    <li>• Municipal code compliance across 12+ jurisdictions</li>
                  </ul>
                </div>
                <Link to="/meet-the-team" className="text-blue-600 underline hover:text-blue-800 font-semibold">
                  Meet our certified Colorado team →
                </Link>
              </div>

              {/* Transparent Pricing & Value Protection */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-8 shadow-lg border border-gray-100">
                <div className="p-3 rounded-lg bg-green-50 w-fit mb-4">
                  <Users className="text-green-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Transparent Pricing & Investment Protection</h3>
                <p className="text-gray-700 mb-4">
                  We're Colorado's only plumbing company showing actual prices online. Our 
                  <Link to="/pricing" className="text-blue-600 underline hover:text-blue-800"> transparent pricing</Link> helps 
                  you budget accurately, while our comprehensive inspections protect your remodel investment by identifying 
                  problems before they become expensive surprises.
                </p>
                <div className="bg-green-50 rounded-lg p-4 mb-4">
                  <p className="text-green-800 font-semibold mb-2">✓ Value Protection:</p>
                  <ul className="text-green-700 text-sm space-y-1">
                    <li>• No hidden fees or surprise charges</li>
                    <li>• Comprehensive pre-remodel inspections</li>
                    <li>• Material upgrades that enhance property value</li>
                    <li>• Long-term performance guarantees</li>
                  </ul>
                </div>
                <Link to="/pricing" className="text-green-600 underline hover:text-green-800 font-semibold">
                  View our transparent pricing →
                </Link>
              </div>

              {/* Advanced Municipal Code Navigation */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-8 shadow-lg border border-gray-100">
                <div className="p-3 rounded-lg bg-purple-50 w-fit mb-4">
                  <Shield className="text-purple-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Municipal Code & Permit Expertise</h3>
                <p className="text-gray-700 mb-4">
                  Each Front Range municipality has different code amendments and permit requirements. We maintain current knowledge 
                  of regulations across Denver, Highlands Ranch, Littleton, Lakewood, Arvada, and other Metro areas, ensuring your 
                  remodel meets all local requirements without delays.
                </p>
                <div className="bg-purple-50 rounded-lg p-4 mb-4">
                  <p className="text-purple-800 font-semibold mb-2">✓ Regulatory Advantages:</p>
                  <ul className="text-purple-700 text-sm space-y-1">
                    <li>• Digital permit portal expertise (eTRAKiT systems)</li>
                    <li>• Established inspector relationships</li>
                    <li>• Code amendment tracking across 12+ cities</li>
                    <li>• Proactive compliance to avoid costly rework</li>
                  </ul>
                </div>
                <div className="text-purple-600 font-semibold text-sm">
                  Current with 2024 IPC adoptions and local amendments
                </div>
              </div>

              {/* Contractor & Homeowner Coordination */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-8 shadow-lg border border-gray-100">
                <div className="p-3 rounded-lg bg-orange-50 w-fit mb-4">
                  <Hammer className="text-orange-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4">Seamless Project Coordination</h3>
                <p className="text-gray-700 mb-4">
                  We work professionally with general contractors, designers, and homeowners to ensure your remodel stays on schedule. 
                  Our experience with both <Link to="/residential-plumbing" className="text-blue-600 underline hover:text-blue-800">residential</Link> and 
                  <Link to="/commercial-plumbing" className="text-blue-600 underline hover:text-blue-800"> commercial projects</Link> includes 
                  coordinating complex multi-trade installations.
                </p>
                <div className="bg-orange-50 rounded-lg p-4 mb-4">
                  <p className="text-orange-800 font-semibold mb-2">✓ Coordination Excellence:</p>
                  <ul className="text-orange-700 text-sm space-y-1">
                    <li>• Precise rough-in and finish scheduling</li>
                    <li>• Designer specification compliance</li>
                    <li>• Multi-trade project experience</li>
                    <li>• Proactive problem-solving to maintain timelines</li>
                  </ul>
                </div>
                <Link to="/reviews" className="text-orange-600 underline hover:text-orange-800 font-semibold">
                  Read contractor testimonials →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Remodel Work Documentation Gallery */}
        <section className="py-20 bg-gradient-to-bl from-blue-900 to-blue-950 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Our Remodel Work Documentation</h2>
              <p className="text-lg text-blue-100">
                See examples of our expert bathroom and kitchen remodel plumbing installations with full documentation across Denver Metro. 
                From luxury fixtures to complex rough-in work.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              
              {/* Rough Plumbing Installation Video */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/rough-plumbing.jpg"
                    alt="Rough Plumbing Installation Castle Rock"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Project Gallery
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Rough Plumbing Installation</h3>
                  <p className="text-blue-100 text-sm">Professional rough plumbing installation for Castle Rock remodel project</p>
                </div>
              </div>

              {/* Bathroom Finish Installation */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/littleton-finish-bathroom.JPEG" 
                    alt="Bathroom Finish Installation Littleton"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Luxury Installation
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Luxury Bathroom Finish Installation</h3>
                  <p className="text-blue-100 text-sm">Professional bathroom finish work in Littleton with high-end fixture installations</p>
                </div>
              </div>

              {/* Shower Pan Replacement */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/replace-shower-pan-arvada-honest-price-actual.JPEG" 
                    alt="Shower Pan Replacement Arvada"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Replacement Project
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Shower Pan Replacement</h3>
                  <p className="text-blue-100 text-sm">Professional shower pan replacement in Arvada with transparent, honest pricing</p>
                </div>
              </div>

              {/* Gas Pipe Rough Plumbing */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/gas-pipe-rough-plumbing-arvada.jpg" 
                    alt="Gas Pipe Rough Plumbing Arvada"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Gas Line Installation
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Gas Pipe Rough Plumbing</h3>
                  <p className="text-blue-100 text-sm">Professional gas pipe rough plumbing installation in Arvada for kitchen remodel appliances</p>
                </div>
              </div>

              {/* Highlands Ranch Basement Project */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/Highlands Ranch Basement Project_20250621_122448_0000.png" 
                    alt="Highlands Ranch Basement Remodel Project"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-purple-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Basement Project
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Highlands Ranch Basement Project</h3>
                  <p className="text-blue-100 text-sm">Complete basement remodel plumbing project in Highlands Ranch with professional installation</p>
                </div>
              </div>

              {/* Vanity Installation */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative aspect-video bg-gray-100">
                  <img 
                    src="/colorado-plumbing-uploads/services/remodels/vanity-install-denver-remodel.JPEG" 
                    alt="Vanity Installation Denver Remodel"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className="bg-indigo-600 text-white px-2 py-1 rounded text-xs font-semibold">
                      Vanity Installation
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold mb-2 text-white">Professional Vanity Installation</h3>
                  <p className="text-blue-100 text-sm">Expert vanity and plumbing installation for Denver remodel with precision fitting</p>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <p className="text-lg text-blue-100 mb-6">
                Every remodel project includes comprehensive documentation with before/after photos and detailed project reporting.
              </p>
              <Link 
                to="/reviews"
                className="inline-flex items-center px-8 py-4 bg-trust-primary text-white rounded-lg font-semibold hover:bg-authority-navy transition-colors"
              >
                View All Project Reviews
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* Customer Reviews Section */}
        <section className="py-20 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="flex items-center justify-center mb-4">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={24} />
                  ))}
                </div>
                <span className="ml-3 text-2xl font-bold text-gray-800">4.9/5 (310+ Reviews)</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Remodel Clients Love Our Expert Service</h2>
              <p className="text-lg text-blue-100">
                See what homeowners and contractors have to say about our bathroom and kitchen remodel plumbing services across Denver Metro.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {/* Review 1 - Bathroom Fixtures Installation (Real Google Review) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border-l-4 border-blue-500">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">Google</span>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Codee is awesome and an experienced plumber. He went above & beyond repairing a leak and installing a new shower pan & bathroom fixtures. He is our 'go-to' guy for any plumbing needs."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Garry C.</p>
                  <p className="text-gray-600">Codee (Owner) - Bathroom Remodel</p>
                </div>
              </div>

              {/* Review 2 - Property Investor Service (Real Google Review) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border-l-4 border-green-500">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">Google</span>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Best plumbing company I've ever used. Always responds promptly with great service fairly priced. It's what a property investor needs to operate effectively."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Charles J.</p>
                  <p className="text-gray-600">Property Investor</p>
                </div>
              </div>

              {/* Review 3 - Toilet Replacement Remodel (Real Angi Review) */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 p-6 shadow-lg border-l-4 border-purple-500">
                <div className="flex items-center mb-4">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">Angi</span>
                </div>
                <p className="text-gray-700 mb-4 italic">
                  "Did you know if your wax seal is any other color than bright yellow, it has leaked? Mine was black, so it had leaked. I wanted to replace it with a low flow toilet that would qualify for a $100 rebate from Denver Water... they were terrific in communicating, finding it from another source, scheduling promptly and just all around really nice guys."
                </p>
                <div className="text-sm">
                  <p className="font-semibold">Toilet Replacement Customer</p>
                  <p className="text-gray-600">Lakewood Project</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <Link 
                to="/reviews"
                className="inline-flex items-center px-8 py-4 bg-trust-primary text-white rounded-lg font-semibold hover:bg-authority-navy transition-colors"
              >
                View All 310+ Reviews
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-trust-primary via-authority-navy to-action-primary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready for Expert Remodel Plumbing?</h2>
              <p className="text-xl mb-8 text-trust-light/90">
                From luxury fixture installations to complex layout changes, we're Denver's trusted remodel plumbing specialists. 
                Professional services available in <Link to="/highlands-ranch-plumber" className="text-action-warm hover:text-white underline">Highlands Ranch</Link>, 
                <Link to="/littleton-plumber" className="text-action-warm hover:text-white underline"> Littleton</Link>, 
                <Link to="/denver-plumber" className="text-action-warm hover:text-white underline"> Denver</Link>, and all Metro areas. 
                Get started with our <Link to="/pricing" className="text-action-warm hover:text-white underline">transparent pricing</Link> and professional coordination.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                
                <button 
                  onClick={handleContactClick}
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                >
                  Schedule Remodel Consultation
                </button>
              </div>
              
              <div className="text-center">
                <p className="text-trust-light/80 text-sm">
                  ✓ Master Plumber Owned  ✓ Luxury Fixture Specialists  ✓ Layout Change Experts  ✓ <Link to="/pricing" className="text-action-warm hover:text-white underline">Transparent Pricing</Link>  ✓ 4.9★ Rating (310+ Reviews)
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default BathroomKitchenRemodel;